// @ts-nocheck

import { clamp } from 'lodash';
import React, { useRef, Children, useState, useEffect, useCallback, useLayoutEffect, useMemo } from 'react';
import type { Node, ElementRef } from 'react';
import { useSprings, animated } from 'react-spring/web.cjs'; // We are using commonJS module to fix iOS 9 issue / https://github.com/babel/babel/issues/9712 / https://github.com/react-spring/react-spring/issues/552
import { useDrag } from 'react-use-gesture';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import { ZIndices } from '../../styled-components/dark/common';
import { KeyCodes } from '../../types/keyboard';
import numberUtils from '../../utils/number';

const { normalize } = numberUtils;

type WrapProps = {
  $index: number;
  $height: number;
  $maxCardHeight: number;
  $disable: boolean;
  $noPadding: boolean;
  $isReady: boolean;
  $fullHeight: boolean;
  $gapBetweenCards: number;
  $childrenClassName: string;
};

const Wrap = styled.div<WrapProps>`
  position: relative;
  overflow: visible;
  cursor: ${({ $disable }) => ($disable ? 'inherit' : 'grab')};
  height: ${({ $height }) => $height}px;

  & > div {
    position: absolute;
    width: 100%;
    ${({ $fullHeight }) =>
      $fullHeight
        ? css`
            height: 100%;
          `
        : undefined}
    will-change: transform;
    box-sizing: border-box;
    user-select: none;

    ${({ $maxCardHeight }) => {
      if ($maxCardHeight) {
        return css`
          height: ${$maxCardHeight}px;
        `;
      }
    }}

    &[data-current='true'] {
      z-index: 1;
    }

    ${({ $gapBetweenCards, $childrenClassName }) => css`
      &.is-last.${$childrenClassName} {
        width: calc(100% + ${$gapBetweenCards * 2}px);
      }
    `}
  }

  ${({ $noPadding }) =>
    !$noPadding
      ? css`
          & > div {
            padding: 1em;
          }
        `
      : undefined}

  transition: opacity 200ms ease-in-out;
  ${({ $isReady }) => css`
    opacity: ${$isReady ? 1 : 0};
  `}
`;

const EventListenerItem = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${ZIndices.navBar - 1};
`;

const SCALE_MAX = 1;
const SCALE_MIN = 0.92;

export type SwipeProps = {
  children: Array<Node>;
  disable?: boolean;
  className?: string;
  setIndex?: (index: number) => void;
  onResize?: ({ width, height }: { width: number; height: number }) => void;
  useKeyboard?: boolean;
  indexToGo?: number | null;
  useDeportedEvent: boolean;
  noScale?: boolean;
  fillSpace?: boolean;
  showNextPage?: boolean;
  noPadding?: boolean;
  setIsLoaded?: (isLoaded: boolean) => void;
  delayShowing?: number;
  controlled?: boolean;
  fullHeight?: boolean;
  alwaysRenderEvery?: boolean;
  alwaysShowEvery?: boolean;
  gapBetweenCards?: number;
  hasSameHeight?: boolean;
  heightCorrection?: number;
  childrenClassName?: string;
};

const Swipe = ({
  children,
  className,
  setIndex,
  disable = false,
  onResize,
  indexToGo = null,
  useKeyboard = !Target.__MOBILE__,
  useDeportedEvent = false,
  noScale = false,
  fillSpace = false,
  showNextPage = false,
  noPadding = false,
  setIsLoaded = () => {},
  delayShowing = 0,
  controlled = false,
  fullHeight = false,
  alwaysRenderEvery = false,
  alwaysShowEvery = false,
  gapBetweenCards = 0,
  hasSameHeight = false,
  heightCorrection = 0,
  childrenClassName,
}: SwipeProps) => {
  const index = useRef<number>(0);
  const ref = useRef<ElementRef<'div'> | null>(null);
  const [refresher, setRefresher] = useState(false);
  const [maxCardHeight, setMaxCardHeight] = useState<number | null>(null);
  const [isReady, setIsReady] = useState(false);

  const [wrapHeight, setWrapHeight] = useState(0);
  const wrapWidth = ref.current?.offsetWidth ?? window.innerWidth;

  useEffect(() => {
    setIsLoaded(isReady);

    return () => {
      setIsLoaded(false);
    };
  }, [isReady, setIsLoaded]);

  useEffect(() => {
    const refresh = () => {
      setRefresher((p) => !p);
    };

    window.addEventListener('resize', refresh);
    return () => {
      window.removeEventListener('resize', refresh);
    };
  }, []);

  const withScaleOrNot = useCallback(
    (data: { x: number; display: string; opacity: number; scale: number }) => {
      if (noScale) {
        return { ...data, scale: SCALE_MAX };
      }
      return data;
    },
    [noScale],
  );

  const reset = useCallback(
    (i: number) => {
      const x = (i - index.current) * (wrapWidth + gapBetweenCards);

      return withScaleOrNot({
        x,
        display: 'block',
        opacity: i === index.current || showNextPage ? 1 : 0,
        scale: i === index.current ? SCALE_MAX : SCALE_MIN,
      });
    },
    [withScaleOrNot, showNextPage, wrapWidth, gapBetweenCards],
  );

  const [items, set] = useSprings(Children.count(children), reset);

  const goToIndex = useCallback(
    (newIndex: number) => {
      const xDirection = newIndex < index.current ? -1 : 1;
      index.current = clamp(newIndex, 0, Children.count(children) - 1);
      if (!controlled && setIndex) {
        setIndex(index.current);
      }
      set((i) => {
        const x = (i - index.current) * (wrapWidth + gapBetweenCards);

        let scale = i === index.current ? SCALE_MAX : SCALE_MIN;
        let opacity = i === index.current || showNextPage ? 1 : 0;
        const down = xDirection === -1;

        if (down && !alwaysShowEvery) {
          if (i === index.current) {
            scale = SCALE_MAX;
            opacity = 1;
          } else {
            scale = SCALE_MIN;
            opacity = showNextPage ? 1 : 0;
          }
        }

        if (!alwaysRenderEvery && (i < index.current - 1 || i > index.current + 1)) return { display: 'none' };

        return withScaleOrNot({ x, scale, display: 'block', opacity });
      });
    },
    [
      children,
      controlled,
      setIndex,
      set,
      wrapWidth,
      gapBetweenCards,
      showNextPage,
      alwaysShowEvery,
      alwaysRenderEvery,
      withScaleOrNot,
    ],
  );

  useEffect(() => {
    const swipe = (e: KeyboardEvent) => {
      if (disable || !useKeyboard) return;
      let xDirection: null | number = null;
      if (e.code === KeyCodes.LEFT_ARROW) {
        xDirection = -1;
      } else if (e.code === KeyCodes.RIGHT_ARROW) {
        xDirection = 1;
      }
      if (xDirection !== null) {
        goToIndex(index.current + xDirection);
      }
    };

    window.addEventListener('keyup', swipe);

    return () => {
      if (useKeyboard) {
        window.removeEventListener('keyup', swipe);
      }
    };
  }, [disable, goToIndex, useKeyboard]);

  useEffect(() => {
    if (indexToGo !== null) {
      goToIndex(indexToGo);
    }
  }, [goToIndex, indexToGo]);

  const bind = useDrag(
    ({ down, movement, swipe: [swipeX] }) => {
      if (!disable) {
        let usedMx = movement[0];
        const usedMy = movement[1];
        // drag "->" === +
        // drag "<-" === -
        if (usedMx < -wrapWidth) usedMx = -wrapWidth - gapBetweenCards;
        if (usedMx > wrapWidth) usedMx = wrapWidth + gapBetweenCards;

        const distanceX = Math.abs(usedMx);
        const distanceY = Math.abs(usedMy);

        // try to avoid horizontal swipe while vertically swiping
        if (distanceY <= distanceX) {
          if (!down) {
            if (swipeX === -1 && index.current < Children.count(children) - 1) {
              index.current++;

              if (setIndex) {
                setIndex(index.current);
              }
            } else if (swipeX === 1 && index.current > 0) {
              index.current--;

              if (setIndex) {
                setIndex(index.current);
              }
            } else {
              if (distanceX > wrapWidth / 2) {
                index.current = clamp(index.current + (usedMx > 0 ? -1 : 1), 0, Children.count(children) - 1);

                if (setIndex) {
                  setIndex(index.current);
                }
              }
            }
          }

          set((i) => {
            if (!alwaysRenderEvery && (i < index.current - 1 || i > index.current + 1)) return { display: 'none' };

            const movementX = down ? usedMx : 0;
            const x = (i - index.current) * (wrapWidth + gapBetweenCards) + movementX;

            let scale = i === index.current ? SCALE_MAX : SCALE_MIN;
            let opacity = i === index.current || showNextPage ? 1 : 0;

            if (down) {
              if (i === index.current) {
                scale = normalize(distanceX, 0, wrapWidth / 2, SCALE_MAX, SCALE_MIN);
                opacity = showNextPage ? 1 : normalize(distanceX, 0, wrapWidth / 2, 1, 0);
              } else {
                scale = normalize(distanceX, 0, wrapWidth / 2, SCALE_MIN, SCALE_MAX);
                opacity = showNextPage ? 1 : normalize(distanceX, 0, wrapWidth / 2, 0, 1);
              }
            }
            return withScaleOrNot({ x, scale, display: 'block', opacity });
          });
        }
      }
    },
    {
      useTouch: true,
      experimental_preventWindowScrollY: true, // NB: it is an experimental react-gesture option. We can have side effects
    },
  );

  useEffect(() => {
    set(reset);
  }, [set, reset]);

  useLayoutEffect(() => {
    if (hasSameHeight) {
      setMaxCardHeight(null);
    }
  }, [children, hasSameHeight]);

  useLayoutEffect(() => {
    const refElement = ref.current;

    if (refElement && !maxCardHeight) {
      const cardElements = Array.from(refElement.children);
      const height = Math.max(...cardElements.map((e) => e.scrollHeight));

      setWrapHeight((h) => {
        const correction = heightCorrection; // px
        const nextHeight = Math.abs((h ?? 0) - height) <= correction ? h : height;

        if (h !== nextHeight) {
          return nextHeight;
        }

        return h;
      });
    }
  }, [refresher, maxCardHeight, children, heightCorrection]);

  useLayoutEffect(() => {
    if (hasSameHeight) {
      setMaxCardHeight(wrapHeight);
    }
  }, [wrapHeight, hasSameHeight]);

  useEffect(() => {
    if (onResize) {
      onResize({ width: wrapWidth, height: wrapHeight });
    }
  }, [onResize, wrapWidth, wrapHeight]);

  const computedChildren = useMemo(
    () =>
      Children.map(children, (child, i) => {
        if (!child) {
          return null;
        }
        if (!alwaysRenderEvery && i < index.current - 1) return null; // do not render far child from current
        const { x, scale, display, opacity } = items[i];

        const classNames = [];

        if (childrenClassName) {
          classNames.push(childrenClassName);
          if (i === Children.count(children) - 1) classNames.push('is-last');
        }

        const className = classNames.length ? classNames.join(' ') : undefined;

        return (
          <animated.div key={i} style={{ x, scale, display }} data-current={i === index.current} className={className}>
            <animated.div style={{ opacity, height: fillSpace ? '100%' : 'auto' }}>{child}</animated.div>
          </animated.div>
        );
      }),
    [children, childrenClassName, items, fillSpace, alwaysRenderEvery],
  );

  useEffect(() => {
    let pid = null;
    if (computedChildren?.length) {
      pid = setTimeout(() => {
        setIsReady(true);
        pid = null;
      }, delayShowing);
    }

    return () => {
      if (pid) {
        clearTimeout(pid);
      }
    };
  }, [computedChildren, delayShowing]);

  return useDeportedEvent ? (
    <>
      <EventListenerItem {...bind()} />
      <Wrap
        $index={index.current}
        ref={ref}
        className={className}
        $height={wrapHeight}
        $maxCardHeight={maxCardHeight}
        $disable={disable}
        $noPadding={noPadding}
        $isReady={isReady}
        $fullHeight={fullHeight}
        $gapBetweenCards={gapBetweenCards}
        $childrenClassName={childrenClassName}
      >
        {computedChildren}
      </Wrap>
    </>
  ) : (
    <Wrap
      $index={index.current}
      ref={ref}
      className={className}
      $height={wrapHeight}
      $maxCardHeight={maxCardHeight}
      $disable={disable}
      $noPadding={noPadding}
      $isReady={isReady}
      $fullHeight={fullHeight}
      $gapBetweenCards={gapBetweenCards}
      $childrenClassName={childrenClassName}
      {...bind()}
    >
      {computedChildren}
    </Wrap>
  );
};

export default React.memo<SwipeProps>(Swipe);
